import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";

const DEFAULT_COLUMN_WIDTH = 150;

const PINNED_Z_INDEX = 100;

const PINNED_HEADER_Z_INDEX = 110;

const BASE_STYLES = {
  "& .MuiDataGrid-toolbarContainer": {
    padding: 1,
    gap: 1,
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto",
    overflowY: "auto",
  },
  "& .MuiDataGrid-columnHeader": {
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  "& .MuiDataGrid-columnHeader[draggable='true']": {
    transition: "opacity 0.2s, transform 0.2s",
  },
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    flex: 1,
    minWidth: 0,
  },
  "& .MuiDataGrid-menuIcon": {
    display: "none",
  },
  "& .MuiDataGrid-iconButtonContainer": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeader.dnd-indicator-left, & .MuiDataGrid-columnHeader.dnd-indicator-right": {
    position: "relative",
    zIndex: 1200,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      width: "4px",
      backgroundColor: "primary.main",
      zIndex: 1201,
      boxShadow: "0 0 12px rgba(25, 118, 210, 0.8)",
    },
  },
  "& .MuiDataGrid-columnHeader.dnd-indicator-left::before": {
    left: 0,
  },
  "& .MuiDataGrid-columnHeader.dnd-indicator-right::before": {
    right: 0,
  },
};

const buildGridColumnWidthMap = (gridColumns) => {
  const map = new Map();
  gridColumns.forEach((col) => {
    if (col.width) {
      map.set(col.field, col.width);
    }
  });
  return map;
};

const getColumnWidth = (field, columnWidths, gridColumnWidthMap) => {
  return (
    columnWidths[field] || gridColumnWidthMap.get(field) || DEFAULT_COLUMN_WIDTH
  );
};

const calculatePinnedColumnWidths = (
  pinnedColumns,
  columnWidths,
  gridColumns
) => {
  const positions = {};
  let cumulativeWidth = 0;
  const gridColumnWidthMap = buildGridColumnWidthMap(gridColumns);
  pinnedColumns.forEach((field) => {
    positions[field] = cumulativeWidth;
    const colWidth = getColumnWidth(field, columnWidths, gridColumnWidthMap);
    cumulativeWidth += colWidth;
  });
  return {
    positions,
    totalWidth: cumulativeWidth,
  };
};

const getMenuColumnStyles = (
  menuColumn,
  isMenuOnLastColumn,
  truncatedColumnsRef
) => {
  if (!menuColumn || !truncatedColumnsRef.current.has(menuColumn)) return {};
  return {
    [`& .MuiDataGrid-columnHeader[data-field='${menuColumn}']`]: {
      overflow: "visible !important",
      zIndex: "1500 !important",
    },
  };
};

const getPinnedColumnStyles = (
  pinnedColumns,
  columnWidths,
  gridColumns,
  pinnedColumnWidths,
  accentColor,
  white
) => {
  const styles = {};
  const pinnedCount = pinnedColumns.length;
  if (pinnedCount === 0) return styles;

  styles["& .MuiDataGrid-virtualScroller"] = {
    overflow: "auto !important",
  };

  styles["& .MuiDataGrid-main"] = {
    position: "relative",
    overflow: "hidden",
  };

  styles["& .MuiDataGrid-columnHeaders"] = {
    position: "relative",
    zIndex: PINNED_HEADER_Z_INDEX + 10,
  };

  styles["& .MuiDataGrid-virtualScrollerContent"] = {
    position: "relative",
  };

  pinnedColumns.forEach((field, index) => {
    const leftPosition = pinnedColumnWidths.positions[field];
    const isLastPinned = index === pinnedCount - 1;

    styles[`& .MuiDataGrid-columnHeader[data-field='${field}']`] = {
      position: "sticky !important",
      left: `${leftPosition}px !important`,
      zIndex: `${PINNED_HEADER_Z_INDEX} !important`,
      backgroundColor: `${white} !important`,
      color: accentColor,
      cursor: "pointer",
      userSelect: "none",
      ...(isLastPinned && {
        boxShadow: "4px 0 6px -2px rgba(0,0,0,0.15)",
      }),
    };

    styles[`& .MuiDataGrid-cell[data-field='${field}']`] = {
      position: "sticky !important",
      left: `${leftPosition}px !important`,
      zIndex: `${PINNED_Z_INDEX} !important`,
      backgroundColor: `${white} !important`,
      color: accentColor,
      ...(isLastPinned && {
        boxShadow: "4px 0 6px -2px rgba(0,0,0,0.15)",
      }),
    };

    styles[
      `& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: "#f5f5f5 !important",
    };
    styles[
      `& .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: "#f5f5f5 !important",
    };

    styles[
      `& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: "#e3f2fd !important",
    };
  });

  if (pinnedCount > 0) {
    const pinnedFieldSelectors = pinnedColumns
      .map((f) => `[data-field='${f}']`)
      .join(", ");

    styles[
      `& .MuiDataGrid-cell:not(${pinnedFieldSelectors
        .split(", ")
        .map((s) => s)
        .join("):not(")})`
    ] = {
      zIndex: "0 !important",
      position: "relative",
    };
  }
  return styles;
};

const getDisplayControlPinnedStyles = (pinnedColumns, theme) => {
  if (pinnedColumns.length === 0) return {};
  const styles = {};
  pinnedColumns.forEach((field) => {
    styles[
      `& .MuiDataGrid-row.row-display-control-detail .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: `${theme.palette.grey[100]} !important`,
    };
    styles[
      `& .MuiDataGrid-row.row-display-control-grand-total .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: `${theme.palette.success.main} !important`,
      color: `${theme.palette.common.white} !important`,
    };
    styles[
      `& .MuiDataGrid-row.row-display-control-sub-total .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: `#2aae65 !important`,
      color: `${theme.palette.common.black} !important`,
    };
    styles[
      `& .MuiDataGrid-row.row-display-control-summary .MuiDataGrid-cell[data-field='${field}']`
    ] = {
      backgroundColor: `#8ed9b0 !important`,
      color: `${theme.palette.common.black} !important`,
    };
  });
  return styles;
};

export const useDataGridStyles = ({
  hasAddFilterPendingChanges,
  isRerunningReport,
  menuColumn,
  isMenuOnLastColumn,
  truncatedColumnsRef,
  pinnedColumns,
  columnWidths,
  gridColumns,
  hasDisplayControl,
}) => {
  const theme = useTheme();
  return useMemo(() => {
    const isPending = hasAddFilterPendingChanges || isRerunningReport;

    const pinnedColumnWidths = calculatePinnedColumnWidths(
      pinnedColumns,
      columnWidths,
      gridColumns
    );
    return {
      height: "100%",
      width: "100%",
      filter: isPending ? "blur(2px)" : "none",
      opacity: isPending ? 0.6 : 1,
      transition: "filter 0.2s ease-out, opacity 0.2s ease-out",
      pointerEvents: isPending ? "none" : "auto",
      ...BASE_STYLES,
      ...getPinnedColumnStyles(
        pinnedColumns,
        columnWidths,
        gridColumns,
        pinnedColumnWidths,
        theme.brand.accent[800],
        theme.brand.white
      ),
      ...(hasDisplayControl
        ? getDisplayControlPinnedStyles(pinnedColumns, theme)
        : {}),
      ...getMenuColumnStyles(
        menuColumn,
        isMenuOnLastColumn,
        truncatedColumnsRef
      ),
    };
  }, [
    hasAddFilterPendingChanges,
    isRerunningReport,
    menuColumn,
    isMenuOnLastColumn,
    truncatedColumnsRef,
    pinnedColumns,
    columnWidths,
    gridColumns,
    hasDisplayControl,
    theme,
  ]);
};
